import React, { useContext, useState } from 'react';
import Header from '../components/Header';
import {
  Box, Button, Container, Grid, Typography, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Collapse, IconButton, TablePagination, Snackbar, Alert, Chip, Tooltip, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { findAllOrders, markOrderAsFulfilled, markOrderAsDelivered, findAllOrderByFilter, updateTrackingInfo } from '../../actions/orderActions';
import { CompanyContext } from '../../CompanyProvider';
import dayjs from 'dayjs';

function OrdersPage({handleLogout}) {
  // Set default fromDate as current date - 60 days, toDate as current date
  const [fromDate, setFromDate] = useState(dayjs().subtract(60, 'day'));
  const [toDate, setToDate] = useState(dayjs());
  const [orderStatus, setOrderStatus] = useState('A'); // Default to 'All'
  const [orders, setOrders] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [trackingOrderID, setTrackingOrderID] = useState(null);
  const [trackingNo, setTrackingNo] = useState('');
  const [trackingWebsite, setTrackingWebsite] = useState('');
  const [trackingError, setTrackingError] = useState(null);

  const companyCode = useContext(CompanyContext);

  React.useEffect(() => {
    const fetchOrders = async () => {
      try {
        const formattedFromDate = fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : '';
        const formattedToDate = toDate ? dayjs(toDate).format("YYYY-MM-DD") : '';
        const data = await findAllOrderByFilter(companyCode, formattedFromDate, formattedToDate, orderStatus);
        setOrders(data);
      } catch (err) {
        setOrders([]);
      }
    };
    fetchOrders();
    // eslint-disable-next-line
  }, []);

  const handleList = async () => {
    // Validation: if fromDate is specified, toDate is mandatory and vice versa
    if ((fromDate && !toDate) || (!fromDate && toDate)) {
      setError("Please select both From Date and To Date.");
      return;
    }
    setError(null); // Clear error if validation passes
    try {
      const formattedFromDate = fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : '';
      const formattedToDate = toDate ? dayjs(toDate).format("YYYY-MM-DD") : '';
      const data = await findAllOrderByFilter(companyCode, formattedFromDate, formattedToDate, orderStatus);
      setOrders(data);
    } catch (err) {
      setOrders([]);
      setError("Failed to fetch orders.");
    }
  };

  const handleExpandClick = (orderID) => {
    setExpanded((prev) => ({ ...prev, [orderID]: !prev[orderID] }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedOrders = orders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const refreshOrders = async () => {
    try {
      const data = await findAllOrders(companyCode);
      setOrders(data);
    } catch (err) {
      console.error('Failed to refresh orders', err);
    }
  };

  const handleMarkFulfillment = async (orderID) => {
    try {
      await markOrderAsFulfilled(companyCode, orderID);
      await refreshOrders();
    } catch (err) {
      console.error('Failed to mark fulfillment', err);
    }
  };

  const handleMarkDelivery = async (orderID) => {
    try {
      await markOrderAsDelivered(companyCode, orderID);
      await refreshOrders();
    } catch (err) {
      console.error('Failed to mark delivery', err);
    }
  };

  const handleOpenTrackingDialog = (orderID) => {
    setTrackingOrderID(orderID);
    setTrackingNo('');
    setTrackingWebsite('');
    setTrackingError(null);
    setTrackingDialogOpen(true);
  };

  const handleCloseTrackingDialog = () => {
    setTrackingDialogOpen(false);
    setTrackingOrderID(null);
    setTrackingNo('');
    setTrackingWebsite('');
    setTrackingError(null);
  };

  const handleSubmitTracking = async () => {
    if (!trackingNo.trim() || !trackingWebsite.trim()) {
      setTrackingError('Both Tracking Number and Tracking Website are required.');
      return;
    }
    try {
      await updateTrackingInfo(companyCode, trackingOrderID, trackingNo.trim(), trackingWebsite.trim());
      handleCloseTrackingDialog();
      await refreshOrders();
    } catch (err) {
      setTrackingError('Failed to update tracking info.');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: '#f1f5f9' }}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Header handleLogout={handleLogout} />
        {/* Error Snackbar */}
        <Snackbar
          open={!!error}
          autoHideDuration={4000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
        <Box sx={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <Container
            sx={{
              pt: 4,
              pb: 4,
              mt: -1.5,
              background: '#ffffff',
              border: '1px solid #e8edf5',
              borderRadius: 4,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 20px rgba(0,0,0,0.04)',
              minWidth: "95%",
              minHeight: '80vh',
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a3c6e', mb: 1.5, mt: 0, letterSpacing: 0.5, fontSize: 22, textAlign: 'left' }}>
              Orders
            </Typography>
            <Divider sx={{ mb: 2, borderColor: '#e2e8f0' }} />
            {/* Filter Section */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4} md={3}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={setFromDate}
                  slotProps={{ textField: { fullWidth: true, size: 'small', InputProps: { sx: { background: '#f8fafc', borderRadius: 2 } } } }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={setToDate}
                  slotProps={{ textField: { fullWidth: true, size: 'small', InputProps: { sx: { background: '#f8fafc', borderRadius: 2 } } } }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3} sx={{width:"20%"}}>
                <FormControl fullWidth size="small">
                  <InputLabel id="order-status-label">Order Status</InputLabel>
                  <Select
                    labelId="order-status-label"
                    id="order-status"
                    value={orderStatus}
                    label="Order Status"
                    onChange={e => setOrderStatus(e.target.value)}
                  >
                    <MenuItem value="A">All</MenuItem>
                    <MenuItem value="P">Pending Payment</MenuItem>
                    <MenuItem value="F">Pending Fulfillment</MenuItem>
                    <MenuItem value="D">Pending Delivery</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <Button variant="contained" color="primary" sx={{ minWidth: 120, background: '#1a3c6e', color: '#fff', fontWeight: 700, borderRadius: 2, boxShadow: '0 2px 8px 0 rgba(26,60,110,0.13)' }} onClick={handleList}>
                  List
                </Button>
              </Grid>
            </Grid>
            {/* Details Section - Table with Pagination */}
            <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <TablePagination
                component="div"
                count={orders.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                  border: 'none',
                  background: '#ffffff',
                  ml: 0,
                  mb: 1,
                  mt: 0,
                  fontWeight: 500
                }}
              />
            </Box>
            <TableContainer component={Paper} sx={{ borderRadius: 2, background: '#ffffff', border: '1px solid #e8edf5', width: '100%', mx: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}></TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}>Order ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}>Order Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}>Created By</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}>Expected Delivery</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}>Delivery Address</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}>Total Amount</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}>Number of Items</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}>Order Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#1a3c6e' }}>Next Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedOrders.map((order) => (
                    <React.Fragment key={order.orderID}>
                      <TableRow>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleExpandClick(order.orderID)}>
                            {expanded[order.orderID] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{fontWeight:"bold", color: '#1a3c6e'}}>
                          <Tooltip title={order.orderID} arrow>
                            <span style={{ cursor: 'pointer', borderBottom: '1px dotted #1a3c6e', color: '#1a3c6e' }}>
                              {order.orderID.slice(-5)}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{order.orderDate}</TableCell>
                        <TableCell>{order.orderCreatedBy}</TableCell>
                        <TableCell>{order.expectedDeliveryDate}</TableCell>
                        <TableCell>
                          <Tooltip title={order.deliveryAddress} arrow>
                            <span style={{ cursor: 'pointer', borderBottom: '1px dotted #1a3c6e', color: '#1a3c6e' }}>
                              {order.deliveryAddress && order.deliveryAddress.length > 18
                                ? order.deliveryAddress.slice(0, 18) + '...'
                                : order.deliveryAddress}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <span style={{ fontWeight: 700, color: '#1a3c6e' }}>
                            {order.usedCoupons && String(order.usedCoupons).trim()
                              && !Number(order.totalCashPaid)
                              && !Number(order.totalPointsPaid)
                              ? 'NA'
                              : `\u20B9 ${order.totalAmount}`}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span style={{ fontWeight: 700, color: '#1a3c6e' }}>{order.items.length}</span>
                        </TableCell>
                        <TableCell>
                          {(() => {
                            let status = 'Ordered';
                            if (order.deliveryDate) {
                              status = 'Delivered';
                            } else if (order.fulfillmentDate) {
                              status = 'Pending Delivery';
                            } else if (order.orderDate) {
                              status = 'Pending Fulfillment';
                            }
                            let color = '#888';
                            let borderColor = '#888';
                            if (status === 'Delivered') {
                              color = '#34A853'; borderColor = '#34A853';
                            } else if (status === 'Pending Delivery') {
                              color = '#F4B400'; borderColor = '#F4B400';
                            } else if (status === 'Pending Fulfillment') {
                              color = '#4285F4'; borderColor = '#4285F4';
                            }
                            return (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Chip
                                  label={status}
                                  variant='outlined'
                                  sx={{
                                    fontWeight: 400,
                                    color,
                                    borderColor,
                                    borderRadius: 10,
                                    fontSize: 14,
                                    px: 1.5,
                                    letterSpacing: 0.2
                                  }}
                                  size="small"
                                />
                                {order.trackingNo && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 1 }}>
                                    <Typography variant="caption" sx={{ color: '#555', fontWeight: 500 }}>Tracking:</Typography>
                                    {order.trackingWebsite ? (
                                      <a
                                        href={order.trackingWebsite}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ fontSize: 12, color: '#1a3c6e', fontWeight: 600, textDecoration: 'underline' }}
                                      >
                                        {order.trackingNo}
                                      </a>
                                    ) : (
                                      <Typography variant="caption" sx={{ color: '#1a3c6e', fontWeight: 600 }}>{order.trackingNo}</Typography>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                              {order.fulfillmentDate == null ? (
                                <Button
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  sx={{ minWidth: 140, borderRadius: 2, textTransform: 'none' }}
                                  onClick={() => handleMarkFulfillment(order.orderID)}
                                >
                                  Mark Fulfillment
                                </Button>
                              ) : order.deliveryDate == null ? (
                                <Button
                                  disableElevation
                                  variant="contained"
                                  color="primary"
                                  size="small"
                                  sx={{ minWidth: 140, borderRadius: 2, textTransform: 'none' }}
                                  onClick={() => handleMarkDelivery(order.orderID)}
                                >
                                  Mark Delivery
                                </Button>
                              ) : null}
                              {order.fulfillmentDate != null && order.deliveryDate == null && (
                                <Button
                                  disableElevation
                                  variant="outlined"
                                  size="small"
                                  sx={{ minWidth: 140, borderRadius: 2, textTransform: 'none', borderColor: '#1a3c6e', color: '#1a3c6e' }}
                                  onClick={() => handleOpenTrackingDialog(order.orderID)}
                                >
                                  Update Tracking
                                </Button>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                          <Collapse in={expanded[order.orderID]} timeout="auto" unmountOnExit>
                            <Box margin={1}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a3c6e', mb: 1 }}>
                                Item Details
                              </Typography>
                              <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>SKU</TableCell>
                                  <TableCell>Item Name</TableCell>
                                  <TableCell>Category</TableCell>
                                  <TableCell>Quantity</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {order.items.map((item) => (
                                  <TableRow key={item.sku}>
                                    <TableCell>{item.sku}</TableCell>
                                    <TableCell>{item.name}</TableCell>
                                    <TableCell>{item.category}</TableCell>
                                    <TableCell>{item.purchaseQuantity}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Container>
        </Box>
        {/* Tracking Info Dialog */}
        <Dialog
          open={trackingDialogOpen}
          onClose={handleCloseTrackingDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '16px',
              background: '#ffffff',
              boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
              border: 'none',
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, color: '#0f172a', borderBottom: '1px solid #e8edf5', fontSize: 18 }}>Update Tracking Info</DialogTitle>
          <DialogContent sx={{ background: '#ffffff', pt: 2 }}>
            {trackingError && (
              <Alert severity="error" sx={{ mb: 2 }}>{trackingError}</Alert>
            )}
            <TextField
              label="Tracking Number"
              value={trackingNo}
              onChange={e => setTrackingNo(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 2, mt: 1 }}
              InputProps={{ sx: { background: '#f8fafc', borderRadius: 2 } }}
            />
            <TextField
              label="Tracking Website"
              value={trackingWebsite}
              onChange={e => setTrackingWebsite(e.target.value)}
              fullWidth
              size="small"
              placeholder="e.g. https://www.fedex.com"
              InputProps={{ sx: { background: '#f8fafc', borderRadius: 2 } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 2, background: '#ffffff', borderTop: '1px solid #e8edf5' }}>
            <Button onClick={handleCloseTrackingDialog} sx={{ textTransform: 'none', color: '#64748b' }}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmitTracking}
              sx={{ fontWeight: 700, borderRadius: 2, textTransform: 'none' }}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </Box>
  );
}

export default OrdersPage;
